import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { services } from '../../mocks/services';
import QuoteModal from '../../components/feature/QuoteModal';
import SchemaOrgHead from '../../components/feature/SchemaOrgHead';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const serviciosSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/servicios`,
  url: `${siteUrl}/servicios`,
  name: 'Servicios de Limpieza Residencial en The Villages, FL | Limpieza Pro',
  description:
    'Conoce todos nuestros servicios de limpieza residencial en The Villages, FL: post-mudanza, limpieza de ventanas, lavado a presión y valet trash para condominios.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Servicios', item: `${siteUrl}/servicios` },
    ],
  },
  mainEntity: [
    {
      '@type': 'Service',
      name: 'Limpieza Post-Mudanza The Villages FL',
      provider: { '@type': 'LocalBusiness', name: 'Limpieza Pro The Villages FL' },
      areaServed: { '@type': 'City', name: 'The Villages' },
      description: 'Limpieza profunda residencial en The Villages, FL para propiedades post-mudanza. Cada rincón, gabinete, electrodoméstico y baño a la perfección.',
    },
    {
      '@type': 'Service',
      name: 'Limpieza de Ventanas The Villages FL',
      provider: { '@type': 'LocalBusiness', name: 'Limpieza Pro The Villages FL' },
      areaServed: { '@type': 'City', name: 'The Villages' },
      description: 'Servicio profesional de limpieza de ventanas interiores y exteriores en The Villages, FL. Sin rayas ni manchas.',
    },
    {
      '@type': 'Service',
      name: 'Lavado a Presión The Villages FL',
      provider: { '@type': 'LocalBusiness', name: 'Limpieza Pro The Villages FL' },
      areaServed: { '@type': 'City', name: 'The Villages' },
      description: 'Lavado a presión de entradas, patios, fachadas y superficies exteriores en The Villages, FL.',
    },
    {
      '@type': 'Service',
      name: 'Valet Trash para Condominios The Villages FL',
      provider: { '@type': 'LocalBusiness', name: 'Limpieza Pro The Villages FL' },
      areaServed: { '@type': 'City', name: 'The Villages' },
      description: 'Servicio de recogida de basura puerta a puerta en condominios y apartamentos de The Villages, FL.',
    },
  ],
};

export default function ServiciosPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = (i18n.language === 'en' ? 'en' : 'es') as 'es' | 'en';

  const whyUs = [
    { icon: 'ri-team-line', tKey: 'svcp_w1_t', dKey: 'svcp_w1_d' },
    { icon: 'ri-leaf-line', tKey: 'svcp_w2_t', dKey: 'svcp_w2_d' },
    { icon: 'ri-customer-service-2-line', tKey: 'svcp_w3_t', dKey: 'svcp_w3_d' },
    { icon: 'ri-secure-payment-line', tKey: 'svcp_w4_t', dKey: 'svcp_w4_d' },
  ];

  return (
    <>
      <SchemaOrgHead
        schema={serviciosSchema}
        pageTitle="Servicios de Limpieza Residencial The Villages, FL | Limpieza Pro"
        pageDescription="Limpieza post-mudanza, limpieza de ventanas, lavado a presión y valet trash en The Villages, FL. Equipo profesional con resultados garantizados."
        canonicalPath="/servicios"
      />
      {/* Page Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/f65a6b22fc6bab20b0bb885694178c0b.jpg"
          alt="Equipo profesional de limpieza"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue-dark/85 to-brand-green-dark/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block bg-white/10 border border-white/20 text-brand-green-light text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">
            {t('svcp_badge')}
          </span>
          <h1 className="font-heading font-bold text-white text-5xl mb-5">{t('svcp_title')}</h1>
          <p className="text-white/75 text-base max-w-xl mx-auto leading-relaxed">
            {t('svcp_desc')}
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 space-y-8">
          {services.map((service, idx) => (
            <div
              key={service.id}
              className={`bg-white rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 ${idx % 2 !== 0 ? 'lg:grid-flow-col-dense' : ''}`}
            >
              {/* Image */}
              <div className={`relative h-72 lg:h-auto ${idx % 2 !== 0 ? 'lg:col-start-2' : ''}`}>
                <img
                  src={service.image}
                  alt={service.title[lang]}
                  className="w-full h-full object-cover object-top"
                />
                {service.tag && (
                  <span className="absolute top-4 left-4 bg-brand-green-dark text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    {service.tag[lang]}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className={`p-10 flex flex-col justify-center ${idx % 2 !== 0 ? 'lg:col-start-1 lg:row-start-1' : ''}`}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-green-light/50 mb-5">
                  <i className={`${service.icon} text-brand-green-dark text-2xl`} />
                </div>
                <h2 className="font-heading font-bold text-brand-blue-dark text-2xl mb-3">{service.title[lang]}</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">{service.fullDesc[lang]}</p>

                {/* Features */}
                <ul className="space-y-2 mb-8">
                  {service.features.map((f) => (
                    <li key={f.es} className="flex items-center gap-3 text-sm text-gray-600">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-green/15 flex-shrink-0">
                        <i className="ri-check-line text-brand-green-dark text-xs" />
                      </div>
                      {f[lang]}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setQuoteOpen(true)}
                  className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white font-semibold px-7 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap self-start"
                >
                  {t('svcp_quote_btn')} <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-brand-blue-dark text-3xl mb-3">{t('svcp_why')}</h2>
            <div className="w-12 h-1 bg-brand-green rounded-full mx-auto" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {whyUs.map((item) => (
              <div key={item.tKey} className="text-center p-6 bg-gray-50 rounded-2xl">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-brand-green-light/50 mx-auto mb-4">
                  <i className={`${item.icon} text-brand-green-dark text-2xl`} />
                </div>
                <h3 className="font-semibold text-brand-blue-dark text-sm mb-2">{t(item.tKey)}</h3>
                <p className="text-gray-400 text-xs leading-relaxed">{t(item.dKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="py-14 bg-gradient-to-r from-brand-green-dark to-brand-blue">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-bold text-white text-2xl">{t('svcp_cta_t')}</h2>
            <p className="text-white/75 text-sm mt-1">{t('svcp_cta_d')}</p>
          </div>
          <button
            onClick={() => setQuoteOpen(true)}
            className="bg-white text-brand-green-dark hover:bg-gray-100 font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 flex items-center gap-2"
          >
            <i className="ri-calculator-line" />
            {t('svcp_cta_btn')}
          </button>
        </div>
      </section>

      <QuoteModal isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </>
  );
}
